import ChevronDown from '@mdi/svg/svg/chevron-down.svg?react';
import ChevronUp from '@mdi/svg/svg/chevron-up.svg?react';
import InfoIcon from '@mdi/svg/svg/information.svg?react';
import { type LocaleDefinition } from '@solinkcorp/locale';
import settings from '@solinkcorp/settings';
import {
  Button,
  DateTimePicker,
  IconWithTooltip,
  NumberField,
  Radio,
  RadioGroup,
  Select,
  TextArea,
} from '@solinkcorp/solink-components';
import EventFilterActions from 'actions/EventFilterActions';
import { type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import { CommonContext } from 'components/Contexts';
import ActivityExpiryControl from 'components/ScheduledActivityForm/ActivityExpiryControl';
import DaysOfWeekPicker from 'components/ScheduledActivityForm/DaysOfWeekPicker';
import {
  AI_CHAT_WEEKLY_SCHEDULE_OPTIONS,
  AI_INSPECTION_END_TYPE,
  type AiInspectionEndType,
  MAX_TIMESTAMP_OFFSET,
  MIN_TIMESTAMP_OFFSET,
  TIME_TARGETS,
  type UseSpotCheckForm,
} from 'components/SpotCheckPanel/hooks/useSpotCheckForm';
import moment from 'moment-timezone';
import React, { useContext } from 'react';
import { useEventReports } from 'src/hooks/useEventReports';
import useLocale from 'src/hooks/useLocale';
import { useSelector } from 'stores/store';
import LocaleUtil from 'utils/LocaleUtil';
import type ReportUtil from 'utils/ReportUtil';
import { getValidationErrorMessage } from 'utils/yupUtils';
import {
  DOES_NOT_REPEAT,
  REPEAT_DAILY,
  REPEAT_EVERY_15MIN,
  REPEAT_EVERY_30MIN,
  REPEAT_EVERY_4H,
  REPEAT_EVERY_6H,
  REPEAT_HOURLY,
} from 'vd-constants';
import styles from './SpotCheckForm.module.sass';
import StatesList from './StatesList';
interface SpotCheckFormProps {
  spotCheck?: ScheduledActivityEntity;
  spotCheckForm: UseSpotCheckForm;
  timezoneId: string;
  formMode: 'create' | 'edit';
  selectedReportId: string[];
  totalEventsInLast30Days: number | undefined;
  isEventsEstimateBelowLimit: boolean;
  eventsMonthlyQuotaLimit: number | undefined;
}
export default function SpotCheckForm(props: SpotCheckFormProps) {
  const {
    spotCheckForm,
    timezoneId,
    formMode,
    totalEventsInLast30Days,
    isEventsEstimateBelowLimit,
    eventsMonthlyQuotaLimit,
  } = props;

  const {
    setAiChatAnswerFormat,
    setAiChatPrompt,
    aiChatPrompt,
    aiChatAnswerFormat,
    startTime,
    setStartTime,
    recurrence,
    recurrenceOptions,
    setRecurrence,
    setWeeklyScheduleOption,
    weeklyScheduleOption,
    selectedDaysOfWeek,
    setSelectedDaysOfWeek,
    endTime,
    setEndTime,
    reoccurringCount,
    setReoccurringCount,
    selectedEndType,
    setSelectedEndType,
    spotCheckValidationError,
    fiveMinutesFromNow,
    setSelectedEventReport,
    selectedEventReport,
    eventTimestampOffset,
    setEventTimestampOffset,
    eventTimeTarget,
    setEventTimeTarget,
  } = spotCheckForm;

  const locale = useLocale();

  const [isScheduledDetailsVisible, setIsScheduledDetailsVisible] = React.useState(false);
  const [isResponseFormatCollapsed, setIsResponseFormatCollapsed] = React.useState(true);

  const spotCheckMode = useSelector((state) => state.filter.spotCheckMode);

  const isLivePreview = spotCheckMode === 'live-preview';
  const isEventSpotCheck = spotCheckMode === 'event';
  const disableEditExpirationType = formMode === 'edit';
  const disableStartTimeCheck = formMode === 'edit';

  const eventReports = useEventReports();

  React.useEffect(() => {
    if (!eventReports?.length) {
      EventFilterActions.fetchFilters();
    }
  }, [eventReports?.length]);

  const reportOptions =
    eventReports?.map((report: ReportUtil) => {
      return {
        value: report.templateId,
        label: report.getName(locale.localeCode),
      };
    }) || [];

  const [selectedReportOption, setSelectedReportOption] = React.useState<string | undefined>();

  const timeTargets = [
    { value: TIME_TARGETS.START, label: locale.scheduledAIChat.start },
    { value: TIME_TARGETS.END, label: locale.scheduledAIChat.end },
  ];

  const {
    authData: { firstDayOfWeek },
  } = useContext(CommonContext);

  const aiChatPromptErrorText = getValidationErrorMessage(spotCheckValidationError, 'aiChatPrompt');

  const startTimeErrorText = getValidationErrorMessage(spotCheckValidationError, 'startTime');

  const handleSelectedReportOption = (e: { value: string }) => {
    setSelectedReportOption(e.value);
    const specReport = eventReports?.find((report) => report.templateId === e.value);
    setSelectedEventReport(specReport);
    setEventTimestampOffset(0);
    setEventTimeTarget(TIME_TARGETS.START);
  };

  const handleSelectedTimeOption = (e: any) => {
    setEventTimeTarget(e.value);
  };

  const handleTimeOffsetChange = (e: any) => {
    if (e < MIN_TIMESTAMP_OFFSET || e > MAX_TIMESTAMP_OFFSET) return;
    setEventTimestampOffset(e);
  };

  React.useEffect(() => {
    if (selectedEventReport?.templateId) {
      setSelectedReportOption(selectedEventReport?.templateId);
    }
  }, [selectedEventReport?.templateId]);

  const reportSelectErrorText =
    !isEventSpotCheck || !selectedEventReport || isEventsEstimateBelowLimit
      ? undefined
      : LocaleUtil.getLocalizedStr({
          string: locale.scheduledAIChat.eventSpotCheckEstimateExceedsQuota,
          replacements: { number: totalEventsInLast30Days, quota: eventsMonthlyQuotaLimit },
        });

  const reportSelectWarningText =
    !isEventSpotCheck || !selectedEventReport || doesReportHaveLocationFilter(selectedEventReport)
      ? undefined
      : locale.scheduledAIChat.eventSpotCheckNoLocationFilterWarning;

  return (
    <form className={styles.spotCheckForm}>
      <div>
        <TextArea
          labelText={`${locale.scheduledAIChat.prompt} *`}
          errorText={aiChatPromptErrorText}
          rows={8}
          autoExpandTextarea={true}
          name="prompt"
          onChange={({ value }: { value: string }) => setAiChatPrompt(value)}
          value={aiChatPrompt}
        />
      </div>

      <div>
        <StatesList spotCheckForm={spotCheckForm} />
      </div>

      {!!aiChatAnswerFormat && (
        <div className={styles.responseFormatSection}>
          <button
            type="button"
            className={styles.responseFormatToggle}
            onClick={() => setIsResponseFormatCollapsed(!isResponseFormatCollapsed)}
          >
            <span>{locale.scheduledAIChat.additionalResponseContext}</span>
            {isResponseFormatCollapsed ? (
              <ChevronDown style={{ width: 20, height: 20 }} />
            ) : (
              <ChevronUp style={{ width: 20, height: 20 }} />
            )}
          </button>
          {!isResponseFormatCollapsed && (
            <div className={styles.responseFormatContent}>
              <TextArea
                name="response-format"
                rows={4}
                autoExpandTextarea={true}
                value={aiChatAnswerFormat}
                labelText={locale.scheduledAIChat.responseFormat}
                onChange={({ value }: { value: string }) => setAiChatAnswerFormat(value)}
              />
            </div>
          )}
        </div>
      )}
      {!isEventSpotCheck && (
        <div className={styles.spotCheckScheduleWrapper}>
          <label>{locale.alarms.schedule}</label>
          {isScheduledDetailsVisible ? (
            <>
              <div className={styles.spotCheckDateTimePickerWrapper}>
                <DateTimePicker
                  locale={locale}
                  calendarStartDay={firstDayOfWeek}
                  timeZoneId={timezoneId}
                  value={new Date(startTime)}
                  onChange={(value) => {
                    setStartTime(moment.tz(value, timezoneId).valueOf());
                  }}
                  minTime={disableStartTimeCheck ? undefined : fiveMinutesFromNow}
                  errorText={startTimeErrorText}
                  hideTimeZoneAbbr
                  name="date"
                  stacked
                />
              </div>
              <div className={styles.frequencySelectWrapper}>
                <div className={styles.frequencySelect}>
                  <Select
                    name="recurrence"
                    value={recurrence}
                    options={recurrenceOptions}
                    onChange={({ value }: { value: string }) => setRecurrence(value)}
                  />
                </div>
                {(recurrence === REPEAT_EVERY_15MIN ||
                  recurrence === REPEAT_EVERY_30MIN ||
                  recurrence === REPEAT_HOURLY ||
                  recurrence === REPEAT_EVERY_6H ||
                  recurrence === REPEAT_EVERY_4H) && (
                  <div className={styles.frequencySelect}>
                    <Select
                      dataTestId="weekly-schedule-option"
                      name="weeklyScheduleOption"
                      options={Object.entries(AI_CHAT_WEEKLY_SCHEDULE_OPTIONS).map(
                        ([key, value]) => ({
                          value: key,
                          label: locale.event[value.text],
                        }),
                      )}
                      onChange={({ value }: { value: string }) =>
                        setWeeklyScheduleOption(value as any)
                      }
                      value={weeklyScheduleOption}
                    />
                    <IconWithTooltip
                      className={styles.weeklyScheduleInfoIcon}
                      iconSize={20}
                      fill="var(--color-dark-gray-lightest)"
                      icon={InfoIcon}
                      title={locale.scheduledAIChat.hoursOfOperationInfo}
                    />
                  </div>
                )}
                {recurrence === REPEAT_DAILY && (
                  <div className={styles.daysOfWeekWrapper}>
                    <DaysOfWeekPicker
                      selectedDaysOfWeek={selectedDaysOfWeek}
                      setSelectedDaysOfWeek={setSelectedDaysOfWeek}
                    />
                  </div>
                )}
              </div>
            </>
          ) : isLivePreview ? (
            <a href={settings.solinkUpgradeUrl} target="_blank" rel="noopener noreferrer">
              <Button
                htmlButtonType="button"
                className={styles.spotCheckScheduleButton}
                colorMode="line-light"
                text={locale.cloudStorage.upgrade}
              />
            </a>
          ) : (
            <Button
              htmlButtonType="button"
              onClick={() => setIsScheduledDetailsVisible(true)}
              className={styles.spotCheckScheduleButton}
              colorMode="line-light"
              text={getFormattedScheduledSummary(
                startTime,
                endTime,
                reoccurringCount,
                selectedEndType,
                timezoneId,
                locale,
              )}
            />
          )}
        </div>
      )}
      {recurrence !== DOES_NOT_REPEAT && isScheduledDetailsVisible && (
        <div className={styles.spotCheckExpirationWrapper}>
          <label>{locale.scheduledAIChat.expiration}</label>
          <ActivityExpiryControl
            selectedEndType={selectedEndType}
            setSelectedEndType={setSelectedEndType}
            timezoneId={timezoneId}
            startTime={startTime}
            updateEndTime={(value) => setEndTime(moment.tz(value, timezoneId).valueOf())}
            endTime={endTime}
            setReoccurringCount={setReoccurringCount}
            reoccurringCount={reoccurringCount}
            reinitializingEndTime={Date.now()}
            disabled={false}
            hasImageCreditsError={false}
            disableEditExpirationType={disableEditExpirationType}
            occurrencesTooltip={
              disableEditExpirationType ? locale.scheduledAIChat.occurrencesInfoTooltip : undefined
            }
          />
        </div>
      )}
      {isEventSpotCheck && (
        <>
          <Select
            labelText={locale.scheduledAIChat.reportsSelectorText}
            options={reportOptions}
            name="Reports"
            onChange={handleSelectedReportOption}
            containerClassName={styles['event-spot-check-report-select']}
            value={selectedReportOption}
            infoIconTooltip={locale.scheduledAIChat.reportsSelectorInfo}
            warningText={reportSelectWarningText}
            errorText={reportSelectErrorText}
            withInfoIcon
          />

          <RadioGroup
            checkedValue={eventTimeTarget}
            isInline={true}
            name="time-targets"
            onChange={handleSelectedTimeOption}
            title={
              <h3>
                {locale.scheduledAIChat.timeTargetsLabel}
                <IconWithTooltip
                  title={locale.scheduledAIChat.timeTargetsInfo}
                  icon={InfoIcon}
                  onClick={(e) => e.preventDefault()}
                  fill={'#A0A0A0'}
                  iconSize={16}
                />
              </h3>
            }
            disabled={!selectedReportOption}
          >
            {timeTargets.map((option) => (
              <Radio key={option.label} labelText={option.label} value={option.value} />
            ))}
          </RadioGroup>
          <div className={styles.spotCheckFormFlexRow}>
            <NumberField
              name="timestamp-offset"
              labelText={
                <h3>
                  {locale.scheduledAIChat.timestampOffsetLabel}
                  <IconWithTooltip
                    title={locale.scheduledAIChat.timestampOffsetInfo}
                    icon={InfoIcon}
                    onClick={(e) => e.preventDefault()}
                    fill={'#A0A0A0'}
                    iconSize={16}
                  />
                </h3>
              }
              onChangeValue={handleTimeOffsetChange}
              value={eventTimestampOffset}
              min={MIN_TIMESTAMP_OFFSET}
              max={MAX_TIMESTAMP_OFFSET}
              disabled={!selectedReportOption}
              inline
            />
          </div>
        </>
      )}
    </form>
  );
}

function getFormattedScheduledSummary(
  startTime: number,
  endTime: number,
  reocurringCount: number,
  endType: AiInspectionEndType,
  timezoneId: string,
  locale: LocaleDefinition,
) {
  const formattedStartTime = moment(startTime).tz(timezoneId).format('LT');
  const formattedStartDate = moment(startTime).tz(timezoneId).isSame(moment(), 'day')
    ? locale.date.today
    : LocaleUtil.getLocalizedStr({
        string: locale.scheduledAIChat.onDate,
        replacements: {
          date: moment(startTime).tz(timezoneId).format('MMM D, YYYY'),
        },
      });

  const expiresMap = {
    [AI_INSPECTION_END_TYPE.date]: LocaleUtil.getLocalizedStr({
      string: locale.scheduledAIChat.expiresOn,
      replacements: {
        date: moment(endTime).tz(timezoneId).format('MMM D, YYYY'),
      },
    }),
    [AI_INSPECTION_END_TYPE.occurrence]: LocaleUtil.getLocalizedStr({
      string: locale.scheduledAIChat.expiresAfter,
      replacements: {
        count: reocurringCount,
      },
    }),
    [AI_INSPECTION_END_TYPE.never]: locale.scheduledAIChat.neverExpires,
  };

  return LocaleUtil.getLocalizedStr({
    string: locale.scheduledAIChat.spotCheckScheduleSummary,
    replacements: {
      start: formattedStartDate,
      time: formattedStartTime,
      expiry: expiresMap[endType],
    },
  });
}

// Naive approach to check for locationId OR locationTags in a report. We are assuming that the report's query structure follows a specific pattern.
// TODO: @dustinirving Once reports are changed in Locations V2 [https://solink.atlassian.net/browse/SL-3870], reference the siteIds + locationTags at the root level of the report instead of extracting from filters.
function doesReportHaveLocationFilter(report: ReportUtil | undefined): boolean {
  const filters = report?.query?.query?.bool?.filter;
  if (!Array.isArray(filters)) return false;

  return filters.some((filter) => {
    const terms = filter['terms'] ?? {};
    const term = filter['term'] ?? {};

    return (
      (Array.isArray(terms.locationId) && terms.locationId.length > 0) ||
      Boolean(term.locationId) ||
      (Array.isArray(terms.locationTags) && terms.locationTags.length > 0) ||
      Boolean(term.locationTags)
    );
  });
}
